import {
    Compartment,
    Deck,
    DeckData,
    JourneyAncillary,
    Row,
    SeatDetail,
    SeatPriceMap,
    SegmentAncillary,
    SegmentSeatData
} from '@/src/types/itinerary'
import { isEmpty } from '../../generalUtils/isEmpty'
import { AncillaryDataStructure, createSegmentInfo, isValidOriginDestination } from './common'

// ================================
// SEAT PROCESSING
// ================================

// Types for better type safety
interface ProcessedSeatPrice {
    prices: Set<number>
    sortedPrices: number[]
    seatPriceMap: SeatPriceMap[]
}

/**
 * Processes seat prices and creates price map
 */
const processSeatPrices = (seatPrices: Set<number>): ProcessedSeatPrice => {
    const sortedPrices = Array.from(seatPrices).sort((a, b) => a - b)
    const seatPriceMap: SeatPriceMap[] = []

    // Handle free seats (price 0)
    if (sortedPrices[0] === 0) {
        seatPriceMap.push({
            start: 0,
            end: 0,
            seatColor: 'seatColors.color0'
        })
        sortedPrices.shift()
    }

    // Process paid seat categories
    const maxCategories = Math.min(sortedPrices.length, 5)

    for (let i = 0; i < maxCategories; i++) {
        seatPriceMap.push({
            start: sortedPrices[i],
            end: sortedPrices[i],
            seatColor: `seatColors.color${i + 1}`
        })
    }

    // Handle overflow category
    if (sortedPrices.length > 5) {
        const lastCategory = seatPriceMap[seatPriceMap.length - 1]
        lastCategory.end = sortedPrices[sortedPrices.length - 1]
        lastCategory.seatColor = 'seatColors.color5'
    }

    return { prices: seatPrices, sortedPrices, seatPriceMap }
}

/**
 * Processes deck data and extracts seat information
 */
const processDeckData = (deckData: Deck): { deckInfo: DeckData | null; hasSeatData: boolean } => {
    if (isEmpty(deckData)) {
        return { deckInfo: null, hasSeatData: false }
    }

    const { compartments, deck, totalColumns, totalRows } = deckData || {}

    if (!Array.isArray(compartments) || isEmpty(compartments)) {
        return { deckInfo: null, hasSeatData: false }
    }

    const seatPrices = new Set<number>()
    const columns: string[] = new Array(totalColumns || 0).fill('')
    const allRows: Row[] = []
    let hasSeatData = false

    // Process all compartments and collect seat data
    compartments.forEach((compartment: Compartment) => {
        const { rows } = compartment || {}

        if (!Array.isArray(rows) || isEmpty(rows)) {
            return
        }

        hasSeatData = true
        allRows.push(...rows)

        // Process seats in each row
        rows.forEach((row: Row) => {
            const { seatDetails, rowId } = row || {}

            if (!Array.isArray(seatDetails) || isEmpty(seatDetails)) {
                return
            }

            seatDetails.forEach((seat: SeatDetail) => {
                // adding a rowId to the seat as BE response doesnt have rowid on seat level.
                //DO NOT REMOVE THIS AS THIS WILL BREAK THE ANCILLARY UPDATE LOGIC.
                seat.rowId = rowId
                if (!seat.isAvailable) {
                    return
                }

                const columnIndex = seat.seatCoordinates.yCoordinate - 1
                seatPrices.add(seat.price)

                if (columnIndex >= 0 && columnIndex < columns.length && isEmpty(columns[columnIndex])) {
                    columns[columnIndex] = seat?.columnId || ''
                }
            })
        })
    })

    if (!hasSeatData) {
        return { deckInfo: null, hasSeatData: false }
    }

    const { seatPriceMap } = processSeatPrices(seatPrices)

    return {
        deckInfo: {
            rows: allRows,
            seatColumns: columns,
            totalColumns: totalColumns || 0,
            totalRows: totalRows || 0,
            deck: deck || '',
            seatPriceMap
        },
        hasSeatData: true
    }
}

/**
 * Processes segment seat data
 */
const processSegmentSeatData = (
    segmentAncillary: SegmentAncillary,
    ancillaryData: AncillaryDataStructure<SegmentSeatData>
): boolean => {
    const { seatAncillaries, originDestinationInfo } = segmentAncillary || {}
    const { aircraftLayout } = seatAncillaries || {}
    const { lowerDeck, upperDeck } = aircraftLayout || {}

    if (!isValidOriginDestination(originDestinationInfo)) {
        return false
    }

    const segmentInfo = createSegmentInfo(originDestinationInfo.from, originDestinationInfo.to)

    // Process both decks
    const lowerDeckResult = processDeckData(lowerDeck)
    const upperDeckResult = upperDeck ? processDeckData(upperDeck) : { deckInfo: null, hasSeatData: false }

    // Skip if no seat data found
    if (!lowerDeckResult.hasSeatData && !upperDeckResult.hasSeatData) {
        return false
    }

    // Build available decks list
    const decks: ('lowerDeck' | 'upperDeck')[] = []
    if (lowerDeckResult.hasSeatData) decks.push('lowerDeck')
    if (upperDeckResult.hasSeatData) decks.push('upperDeck')

    // Add to results
    ancillaryData.availableSegments.push(segmentInfo)
    ancillaryData.segmentWiseAncillaryData[segmentInfo.key] = {
        metaData: { decks },
        lowerDeck: lowerDeckResult.deckInfo,
        upperDeck: upperDeckResult.deckInfo
    }

    return true
}

/**
 * Processes seat ancillaries
 */
export const getSeatAncillaries = (journeyAncillaries: JourneyAncillary[]) => {
    const ancillaryData: AncillaryDataStructure<SegmentSeatData> = {
        availableSegments: [],
        segmentWiseAncillaryData: {}
    }

    if (isEmpty(journeyAncillaries) || !Array.isArray(journeyAncillaries)) {
        return { seatAncillaries: ancillaryData, isSeatAvailable: false }
    }

    let isSeatAvailable = false

    journeyAncillaries.forEach(journeyAncillary => {
        const { segmentAncillaries } = journeyAncillary || {}

        if (!Array.isArray(segmentAncillaries) || isEmpty(segmentAncillaries)) {
            return
        }

        segmentAncillaries.forEach(segmentAncillary => {
            const hasSeats = processSegmentSeatData(segmentAncillary, ancillaryData)
            if (hasSeats) {
                isSeatAvailable = true
            }
        })
    })

    return { seatAncillaries: ancillaryData, isSeatAvailable }
}

/**
 * Finds seat object by seat number within a specific segment
 */
export const findSeatByNumber = (
    seatNumber: string,
    segmentKey: string,
    seatAncillaries: AncillaryDataStructure<SegmentSeatData>
): SeatDetail | undefined => {
    const segmentSeatData = seatAncillaries.segmentWiseAncillaryData[segmentKey]

    if (!segmentSeatData) {
        return
    }

    // Search in lower deck
    if (segmentSeatData.lowerDeck?.rows) {
        for (const row of segmentSeatData.lowerDeck.rows) {
            if (row.seatDetails) {
                for (const seat of row.seatDetails) {
                    if (seat.seatNumber === seatNumber) {
                        return seat
                    }
                }
            }
        }
    }

    // Search in upper deck
    if (segmentSeatData.upperDeck?.rows) {
        for (const row of segmentSeatData.upperDeck.rows) {
            if (row.seatDetails) {
                for (const seat of row.seatDetails) {
                    if (seat.seatNumber === seatNumber) {
                        return seat
                    }
                }
            }
        }
    }

    return
}
